import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { InfoCircle, People, Shop } from "iconsax-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getGetMeQueryKey,
  getGetStoresQueryKey,
  useAcceptStoreInvite,
  useGetPublicInvite,
} from "@/api";
import { SefanoButton } from "@/components/Molecules";
import { getApiErrorMessage } from "@/lib/api/errors";
import { setPostLoginRedirect } from "@/lib/auth/postLoginRedirect";
import { resolveMediaUrl } from "@/lib/media";
import { getRoleDefinition, resolveRoleKeyFromRoleName } from "@/lib/settings/access";
import { useAuthStore } from "@/stores/auth.store";
import { useSessionStore } from "@/stores/session.store";

export function InvitePage({ token }: { token: string }) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setActiveStoreId = useSessionStore((state) => state.setActiveStoreId);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const { data, isLoading, isError } = useGetPublicInvite(token, { query: { retry: false } });
  const invite = data?.status === 200 ? data.data.data : undefined;

  const { mutate: accept, isPending } = useAcceptStoreInvite({
    mutation: {
      onSuccess: async (res) => {
        if (res.status !== 200) return;
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getGetStoresQueryKey() }),
        ]);
        setActiveStoreId(res.data.data.store_id);
        void navigate({ to: "/" });
      },
      onError: (error) => setAcceptError(getApiErrorMessage(error) ?? t("invite.failed")),
    },
  });

  const onAccept = () => {
    setAcceptError(null);
    if (!isAuthenticated) {
      setPostLoginRedirect(`/invite/${token}`);
      void navigate({ to: "/otp" });
      return;
    }
    accept({ token });
  };

  const role = invite
    ? getRoleDefinition(resolveRoleKeyFromRoleName(invite.role_name) ?? "operator")
    : null;
  const RoleIcon = role?.icon ?? People;
  const blockedMessage = !invite
    ? t("invite.notFound")
    : invite.is_accepted
      ? t("invite.accepted")
      : invite.is_expired
        ? t("invite.expired")
        : null;
  const logoUrl = resolveMediaUrl(invite?.store_logo_url);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          bgcolor: "background.paper",
          borderRadius: 2.5,
          border: `1px solid ${theme.palette.divider}`,
          p: { xs: 2.5, md: 3.5 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          textAlign: "center",
        }}
      >
        {isLoading ? (
          <CircularProgress size={30} />
        ) : isError || blockedMessage ? (
          <>
            <InfoCircle size={44} variant="Bulk" color={theme.palette.warning.main} />
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 700 }}>
              {blockedMessage ?? t("invite.notFound")}
            </Typography>
            <SefanoButton onClick={() => void navigate({ to: "/" })} sx={{ width: "100%" }}>
              {t("invite.goToDashboard")}
            </SefanoButton>
          </>
        ) : invite && role ? (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 2,
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }}
            >
              {logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt=""
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Shop size={32} variant="Bold" color={theme.palette.primary.main} />
              )}
            </Box>
            <Box>
              <Typography component="h1" sx={{ fontSize: "1.0625rem", fontWeight: 800 }}>
                {t("invite.title")}
              </Typography>
              <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary", mt: 0.5 }}>
                {t("invite.subtitle", { store: invite.store_name })}
              </Typography>
            </Box>

            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                borderRadius: 1.5,
                textAlign: "start",
                bgcolor: alpha(theme.palette[role.tone].main, 0.06),
                border: `1px solid ${alpha(theme.palette[role.tone].main, 0.2)}`,
              }}
            >
              <RoleIcon size={24} variant="Bold" color={theme.palette[role.tone].main} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary" }}>
                  {t("invite.role")}
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700 }}>
                  {t(role.labelKey)}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.6875rem", color: "text.secondary", lineHeight: 1.7 }}
                >
                  {t(role.descriptionKey)}
                </Typography>
              </Box>
            </Box>

            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {t("invite.phone")}:{" "}
              <Box component="span" dir="ltr" sx={{ fontWeight: 700, color: "text.primary" }}>
                {invite.phone_masked}
              </Box>
            </Typography>

            {acceptError ? (
              <Typography
                role="alert"
                sx={{ fontSize: "0.75rem", fontWeight: 600, color: "error.main" }}
              >
                {acceptError}
              </Typography>
            ) : null}

            <SefanoButton disabled={isPending} onClick={onAccept} sx={{ width: "100%" }}>
              {isPending
                ? t("invite.accepting")
                : isAuthenticated
                  ? t("invite.accept")
                  : t("invite.loginToAccept")}
            </SefanoButton>
          </>
        ) : null}
      </Box>
    </Box>
  );
}
